import axios from "axios";
import { MIN_SEARCH_LENGTH } from "./searchPolicy";
import { extractError, normalizeError, loadRejection } from "../../utils/apiErrorUtils";
import { UPLOAD_TIMEOUT_MS } from "../../utils/axiosTimeouts";
import { mergeAuthTrace, sanitizeExchangeError } from "../../lib/authTrace";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

/** Thrown when the bridge needs a date of birth before it will create an account. */
export class DateOfBirthRequiredError extends Error {
  constructor(message, suggestedName = null) {
    super(message || "Date of birth is required to create an account.");
    this.name = "DateOfBirthRequiredError";
    this.code = "DOB_REQUIRED";
    // The provider's profile name, to prefill the sign-up step (P1-6). The
    // server never derives it from the email; null when there is none.
    this.suggestedName = suggestedName;
  }
}

/**
 * Thrown when the bridge refuses the address itself (400 EMAIL_NOT_ACCEPTED):
 * Supabase verified it, but the account rule does not take it. The message is
 * the server's own sentence, written for the user, and deliberately not copied
 * here as well, so the two cannot drift. Distinct from a failure because a
 * retry cannot help: only a different address can.
 */
export class EmailNotAcceptedError extends Error {
  constructor(message) {
    super(message);
    this.name = "EmailNotAcceptedError";
    this.code = "EMAIL_NOT_ACCEPTED";
  }
}

export const syncSupabaseToApp = async (accessToken, dateOfBirth, name) => {
  try {
    const { data } = await axios.post(`${apiUrl}/users/supabase-session`, {
      access_token: accessToken,
      // Only sent when we have it. The server requires it for NEW accounts only;
      // existing accounts bridge unchanged.
      ...(dateOfBirth ? { dateOfBirth } : {}),
      // The name the collector chose on the sign-up step ({ first, last }).
      // Used for a NEW account only; an existing account keeps its name.
      ...(name ? { name } : {}),
    });
    return data;
  } catch (error) {
    const httpStatus = error?.response?.status;
    const message = extractError(error, "Could not complete sign-in");
    mergeAuthTrace({
      bridgeHttpStatus: httpStatus ?? null,
      exchangeErrorMessage: sanitizeExchangeError(message),
    });
    // Distinct type so the caller can prompt for a date instead of showing a
    // sign-in failure. This is not an error the user did anything wrong to hit.
    if (httpStatus === 422 && error?.response?.data?.code === "DOB_REQUIRED") {
      return Promise.reject(
        new DateOfBirthRequiredError(message, error?.response?.data?.suggestedName || null)
      );
    }
    if (httpStatus === 400 && error?.response?.data?.code === "EMAIL_NOT_ACCEPTED") {
      return Promise.reject(new EmailNotAcceptedError(message));
    }
    return Promise.reject(message);
  }
};

export const login = async (user) => {
  try {
    const { data } = await axios.post(`${apiUrl}/users/login`, user);
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Login failed"));
  }
};

export const signup = async (normalizedUser) => {
  try {
    const { data } = await axios.post(
      `${apiUrl}/users/registration`,
      normalizedUser
    );
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Signup failed"));
  }
};

export const GetUser = async (userId) => {
  try {
    const { data } = await axios.get(`${apiUrl}/users/${userId}`);
    return data;
  } catch (error) {
    return Promise.reject(loadRejection(error, "Failed to get user"));
  }
};

// Re-exported for callers already importing it from here. It lives in
// searchPolicy so a component can import it without pulling axios into its
// Jest bundle.
export { MIN_SEARCH_LENGTH };

/**
 * Search the user directory.
 *
 * Matching happens server-side now. This used to download every active user and
 * filter them in the browser, which is why the endpoint could not be paginated:
 * a server-side cap would have silently made everyone past it unsearchable.
 */
export const GetAllUsers = async (userId, query = "", { page = 1 } = {}) => {
  const term = (query || "").trim();
  // Save the round trip the server would answer with an empty list anyway.
  if (term.length < MIN_SEARCH_LENGTH) return [];
  try {
    const { data } = await axios.get(`${apiUrl}/users/allusers/${userId}`, {
      params: { q: term, page },
    });
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to fetch users"));
  }
};

export const EditUser = async (user) => {
  try {
    const serverUser = { ...user };
    const { _id } = serverUser;
    delete serverUser._id;
    const { data } = await axios.put(`${apiUrl}/users/edit/${_id}`, serverUser);
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to update user"));
  }
};

/**
 * Upload a new profile photo through the same secure pipeline as every other
 * image (#151): POST /upload/photos re-encodes the file, runs it through AI
 * moderation, and stores it. Returns the hosted URL. The Settings screen sets
 * this as the user's avatar on save — the user never types or sees a raw URL.
 */
export const uploadProfilePhoto = async (file) => {
  try {
    const formData = new FormData();
    formData.append("photos", file);
    const { data } = await axios.post(`${apiUrl}/upload/photos`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      // Same endpoint, same re-encode and moderation pass, so the same
      // exemption from the global default (#214) — one photo rather than five,
      // but a 5 MB file on a bad uplink still outlasts a request deadline sized
      // for JSON.
      timeout: UPLOAD_TIMEOUT_MS,
    });
    const url = data?.photos?.[0]?.url;
    if (!url) throw new Error("Upload did not return a photo URL");
    return url;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to upload profile photo"));
  }
};

/**
 * Give back a photo this account uploaded but never used ([#100]).
 *
 * `uploadProfilePhoto` stores the image immediately — it has to, so it can be
 * re-encoded and moderated — which means a photo the user then backs out of is
 * already public and already charged to their 200MB quota. Nothing on the client
 * could release it: POST /upload/photos/retire is the endpoint that lets it.
 *
 * The server only retires rows this account owns. It also skips anything still
 * referenced, but `stillReferenced` consults exactly two surfaces --
 * `UserCard.photos` and `User.image.url` (uploadRetirementService.js:81-99) --
 * so that check is NOT total: a url whose only live reference is a
 * `TradeRequest.photos[]` snapshot is not protected by it. Pass only urls this
 * caller just minted and has not attached to anything.
 *
 * A refusal raises no toast: a best-effort cleanup after the user has already
 * walked away is not theirs to hear about (AvatarUploadField, B-31).
 */
export const retireProfilePhotos = async (urls) => {
  try {
    const { data } = await axios.post(`${apiUrl}/upload/photos/retire`, {
      urls,
    }, { quietRefusal: true });
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to release that photo"));
  }
};

export const GetUserFriends = async (userId) => {
  try {
    const { data } = await axios.get(`${apiUrl}/users/friends/${userId}`);
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to get friends"));
  }
};

export const GetUserCart = async (userId) => {
  try {
    const { data } = await axios.get(`${apiUrl}/cart/${userId}`);
    let normalizedCart = [].concat(...data);
    normalizedCart = normalizedCart.map((item) => item._id);
    return normalizedCart;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to get cart"));
  }
};

export const addToCart = async (userId, card_id) => {
  try {
    const { data } = await axios.put(`${apiUrl}/cart/add/${userId}`, {
      _id: userId,
      cardId: card_id,
    });
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to add to cart"));
  }
};

export const addAllToCart = async (cartItems, userID) => {
  try {
    const { data } = await axios.put(`${apiUrl}/cart/addAll/${userID}`, {
      cartItems,
    });
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to add items to cart"));
  }
};

export const removeFromCart = async (userId, card_id) => {
  try {
    const { data } = await axios.delete(`${apiUrl}/cart/remove/${userId}`, {
      data: {
        _id: userId,
        cardId: card_id,
      },
    });
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to remove from cart"));
  }
};

export const addDiscountToUser = async (userID) => {
  try {
    const { data } = await axios.put(
      `${apiUrl}/cart/addDiscountToPrizes/${userID}`
    );
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to add discount"));
  }
};

export const getPrizes = async (userID) => {
  try {
    const { data } = await axios.get(`${apiUrl}/cart/prizes/${userID}`);
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to get prizes"));
  }
};

// Pass `follow` (true or false) to set the state rather than flip it, so a
// request the WebView sends twice cannot undo itself (B-32).
export const toggleFollowUser = async (currentUserId, targetUserId, follow) => {
  try {
    const { data } = await axios.put(
      `${apiUrl}/users/follow/${targetUserId}`,
      typeof follow === "boolean" ? { follow } : currentUserId
    );
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to follow/unfollow"));
  }
};

export const verifyEmail = async (token) => {
  try {
    const { data } = await axios.get(`${apiUrl}/users/verify/${token}`);
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Email verification failed"));
  }
};

// Signed-in resend. Sends no address: the server takes it from the token, which
// is also why this one cannot help the person who needs it most — see below.
export const resendVerification = async () => {
  try {
    const { data } = await axios.post(`${apiUrl}/users/resend-verification`);
    return data;
  } catch (error) {
    return Promise.reject(
      extractError(error, "Failed to resend verification email")
    );
  }
};

// Signed-OUT resend, for an account that cannot log in *because* it is
// unverified. The server answers one constant string whether or not the address
// exists, so there is nothing here to branch on — callers should show whatever
// comes back and never infer that an account was found.
/**
 * Where collectors actually are, worldwide.
 *
 * The answer to an empty map. Returns `[{ coordinates, collectors,
 * precisionKm }]` — coarse grid cells and counts, never users. Takes no
 * position, so unlike /users/nearby it is not refused outside the launch area:
 * it is specifically for the caller standing where the beta has nobody.
 */
/**
 * Flags the server decides, fetched once at startup.
 *
 * Resolves to `{}` rather than rejecting when it fails: the app must still
 * start if this endpoint is unreachable, and falling back to the compiled-in
 * default is exactly what happened before the endpoint existed.
 */
export const getClientSettings = async () => {
  try {
    const { data } = await axios.get(`${apiUrl}/settings/client`);
    return data || {};
  } catch {
    return {};
  }
};

export const getCollectorHotspots = async () => {
  try {
    const { data } = await axios.get(`${apiUrl}/users/collector-hotspots`);
    return Array.isArray(data?.hotspots) ? data.hotspots : [];
  } catch (error) {
    return Promise.reject(
      extractError(error, "Could not look up collector hotspots")
    );
  }
};

export const requestVerificationEmail = async (email) => {
  try {
    const { data } = await axios.post(
      `${apiUrl}/users/resend-verification-request`,
      { email }
    );
    return data;
  } catch (error) {
    return Promise.reject(
      extractError(error, "Failed to resend verification email")
    );
  }
};

export const forgotPassword = async (email) => {
  try {
    const { data } = await axios.post(`${apiUrl}/users/forgot-password`, {
      email,
    });
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to send reset email"));
  }
};

export const resetPassword = async (token, password) => {
  try {
    const { data } = await axios.post(
      `${apiUrl}/users/reset-password/${token}`,
      { password }
    );
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to reset password"));
  }
};

export const checkResetToken = async (token) => {
  try {
    const { data } = await axios.get(
      `${apiUrl}/users/check-reset-token/${token}`
    );
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Invalid or expired reset token"));
  }
};

export const getNearbyUsers = async (lng, lat, radius = 50000) => {
  try {
    const { data } = await axios.get(
      `${apiUrl}/users/nearby?lng=${lng}&lat=${lat}&radius=${radius}`,
      // quietForbidden: the 403 launch-area refusal is shown in place by the
      // map, so it gets no toast on top (E2E audit 15·9c); an outage still
      // does, because not every map state shows it (release review).
      { quietForbidden: true }
    );
    return data;
  } catch (error) {
    // Rejects with { message, status, retryable } rather than a bare string:
    // the map must tell a 429 apart from a network failure to render an
    // honest empty state (#82).
    return Promise.reject(normalizeError(error, "Failed to get nearby users"));
  }
};

// The map writes this on its own when you have moved, so a refusal raises no
// toast (B-31). An outage still does, as it always did.
export const updateUserLocation = async (longitude, latitude) => {
  try {
    const { data } = await axios.put(`${apiUrl}/users/location`, {
      longitude,
      latitude,
    }, { quietRefusal: true });
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to update location"));
  }
};

export const getLocationPrivacy = async () => {
  try {
    const { data } = await axios.get(`${apiUrl}/users/location-privacy`);
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to get location privacy"));
  }
};

export const updateLocationPrivacy = async (settings) => {
  try {
    const { data } = await axios.put(`${apiUrl}/users/location-privacy`, settings);
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to update location privacy"));
  }
};

export const deleteMyAccount = async () => {
  try {
    const { data } = await axios.delete(`${apiUrl}/users/me`);
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to delete account"));
  }
};

export const getMyPreferences = async () => {
  try {
    const { data } = await axios.get(`${apiUrl}/users/me/preferences`);
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to get preferences"));
  }
};

export const updateMyPreferences = async (preferences) => {
  try {
    const { data } = await axios.put(`${apiUrl}/users/me/preferences`, preferences);
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to update preferences"));
  }
};

/**
 * Terms/Privacy acceptance for the signed-in account (#65).
 *
 * The OAuth path creates accounts with no consent on file — deliberately, since
 * fabricating an acceptance nobody gave would be worse than having none — so the
 * first-run gate asks here whether this account still owes one. The accept call
 * sends no timestamp: the server stamps its own clock, because a consent record
 * whose date the client chose is not evidence of anything.
 */
export const getMyConsent = async () => {
  try {
    const { data } = await axios.get(`${apiUrl}/users/me/consent`);
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(extractError(error, "Could not check your consent status"));
  }
};

/**
 * `policyVersion` is the version the screen showed: both the first-run screen
 * and Q-20's update screen pass legalContent.version. Still never a timestamp.
 * The server records it only if it is the current version and refuses any
 * other named one with a 400, so an acceptance cannot be stamped against text
 * the user never saw. Version 9 passes nothing; the server records that as the
 * older 2026-09-26 and asks again later.
 */
export const acceptTerms = async (policyVersion) => {
  try {
    const body = policyVersion === undefined ? {} : { policyVersion };
    const { data } = await axios.post(`${apiUrl}/users/me/consent`, body);
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(extractError(error, "Could not record your acceptance"));
  }
};
